import * as THREE from 'three';
import { Physics } from '../physics/Physics';
import { World } from '../world/World';
import { Panda, BALL_RADIUS } from '../player/Panda';
import {
  PandaController,
  type ControllerState,
  type ControllerEvents,
} from '../player/PandaController';
import { ChaseCamera } from '../camera/ChaseCamera';
import { InputManager } from '../input/InputManager';
import { Scoring } from '../scoring/Scoring';
import { Mood } from '../scoring/Mood';
import { AudioEngine } from '../audio/Audio';
import { Particles } from '../fx/Particles';
import { HUD } from '../ui/HUD';
import { Menus } from '../ui/Menus';
import { PandaViewer } from '../ui/PandaViewer';
import { OrientationGate } from '../ui/OrientationGate';
import { LEVELS } from '../levels/levels';
import { clamp } from '../util/math';
import type { CollectibleTier, LevelData } from '../levels/types';

type GameState = 'title' | 'playing' | 'paused' | 'finished';

const FIXED_STEP = 1 / 60;
const MAX_SUBSTEPS = 4;

const TIER_COLOR: Record<CollectibleTier, string> = {
  fruit: '#ff6b3d',
  food: '#ffe1a0',
  coin: '#ffd23d',
  envelope: '#ff5d5d',
};

export class Game {
  private renderer!: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private physics = new Physics();
  private input!: InputManager;
  private scoring = new Scoring();
  private mood = new Mood();
  private audio = new AudioEngine();
  private fx = new Particles();
  private world!: World;
  private panda!: Panda;
  private controller!: PandaController;
  private chase!: ChaseCamera;
  private hud!: HUD;
  private menus!: Menus;
  private pandaViewer = new PandaViewer();
  private gate!: OrientationGate;
  private sun!: THREE.DirectionalLight;

  private state: GameState = 'title';
  private currentLevel: LevelData = LEVELS[2]; // Bamboo Descent by default
  private controllerEvents!: ControllerEvents;
  private runTime = 0;
  private acc = 0;
  private last = 0;
  private lastState!: ControllerState;
  private isMobile = false;
  private touchCapable = false;
  private clock = 0;

  async init(): Promise<void> {
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    const uiRoot = document.getElementById('ui-root') as HTMLElement;
    this.isMobile = matchMedia('(pointer: coarse)').matches;
    this.touchCapable =
      this.isMobile || (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !this.isMobile,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.isMobile ? 1.5 : 2));
    // Shadows on desktop only, to hold the mobile framerate floor (§10).
    this.renderer.shadowMap.enabled = !this.isMobile;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Gradient sky (deep blue up high -> pale haze at the horizon) plus fog in
    // the horizon colour, so distant mountains fade in instead of ending in a
    // hard edge against flat blue.
    const horizon = 0xcfe6f2;
    this.scene.background = this.makeSkyTexture(0x4f8fd0, horizon);
    this.scene.fog = new THREE.Fog(horizon, 120, 460);

    // Lighting.
    const hemi = new THREE.HemisphereLight(0xcfeaff, 0x4a6b3f, 0.95);
    this.scene.add(hemi);
    this.sun = new THREE.DirectionalLight(0xfff3d6, 1.6);
    this.sun.position.set(20, 40, 10);
    if (!this.isMobile) {
      this.sun.castShadow = true;
      this.sun.shadow.mapSize.set(1024, 1024);
      const c = this.sun.shadow.camera;
      c.near = 1;
      c.far = 120;
      c.left = c.bottom = -24;
      c.right = c.top = 24;
    }
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    // Physics + world.
    await this.physics.init();
    this.world = new World(this.currentLevel, this.physics);
    this.scene.add(this.world.group);
    this.scene.add(this.fx.points);

    // Panda + controller.
    this.panda = new Panda();
    this.scene.add(this.panda.root);
    this.controllerEvents = this.makeControllerEvents();
    this.controller = new PandaController(this.physics, this.world, this.scoring, this.controllerEvents);
    this.lastState = this.controller.state;

    // Camera.
    this.chase = new ChaseCamera(window.innerWidth / window.innerHeight);
    this.chase.snap(this.controller.state.pos, this.world.startFrame());

    // UI.
    this.gate = new OrientationGate(uiRoot);
    this.hud = new HUD(uiRoot);
    this.menus = new Menus(
      uiRoot,
      LEVELS.map((l) => l.name),
      {
        onStartLevel: (i) => {
          if (LEVELS[i] !== this.currentLevel) this.loadLevel(LEVELS[i]);
          this.startRun();
        },
        onResume: () => this.resume(),
        onRestart: () => this.startRun(),
        onQuit: () => this.quitToTitle(),
      },
    );

    // Input.
    this.input = new InputManager(uiRoot);
    // The pause button routes through the input layer's pause edge (fires once
    // on pointerdown for mouse & touch alike). A separate `click` handler here
    // would double-toggle — pause then immediately resume — so it's intentionally
    // absent.
    this.input.touch.setPauseButton(this.menus.pauseButton);
    this.input.onDeviceChange = (d) => this.onDeviceChange(d);
    // On touch devices, default the active device to touch so the on-screen
    // controls appear during play (otherwise they'd stay hidden until a touch
    // is detected — which can't happen while they're hidden).
    this.input.lastDevice = this.touchCapable ? 'touch' : 'keyboard';
    this.input.touch.setVisible(false); // hidden on the title screen

    window.addEventListener('resize', () => this.onResize());
    this.onResize();
    this.menus.showTitle();
    this.mountTitlePanda();

    this.last = performance.now();
    requestAnimationFrame((t) => this.tick(t));
  }

  private makeControllerEvents(): ControllerEvents {
    return {
      onJump: () => this.audio.jump(),
      onLand: (i) => {
        this.panda.squashImpulse(0.25 + i * 0.5);
        this.audio.land(i);
      },
      onBounce: (pos) => {
        this.panda.squashImpulse(0.7);
        this.audio.bounce();
        this.fx.burst(pos, new THREE.Color(0xff8fc0), 24, 6, 0.7);
      },
      onPickup: (item, result) => {
        this.fx.burst(item.pos, new THREE.Color(TIER_COLOR[item.tier]), 16, 5, 0.6);
        const jackpot = item.tier === 'coin' || item.tier === 'envelope';
        this.audio.pickup(result.streak, jackpot);
        this.popupAt(item.pos, `+${result.gained}`, TIER_COLOR[item.tier]);
        this.mood.feed(item.tier);
      },
      onObstacle: (pos) => {
        this.audio.thud();
        this.chase.addShake(0.5);
        this.fx.burst(pos.clone().setY(pos.y + 0.5), new THREE.Color(0xbbbbbb), 12, 4, 0.5);
        this.mood.upset(0.08);
      },
      onBamboo: (pos) => {
        // Light, friendly: the stalk topples (World) + a few leaves + soft tick.
        this.audio.land(0.2);
        this.fx.burst(pos.clone().setY(pos.y + 1), new THREE.Color(0x8fd46a), 6, 3, 0.5);
        this.mood.upset(0.025);
      },
      onFall: (pos) => {
        this.audio.fall();
        this.chase.addShake(0.4);
        this.popupAt(pos, 'Oops!', '#ff8a8a');
        this.mood.upset(0.22);
      },
      onFinish: () => this.finish(),
      onSurface: (s) => this.hud.setSurface(s.label),
    };
  }

  /** Tear down the current course and build a new one (default or generated). */
  private loadLevel(level: LevelData): void {
    this.scene.remove(this.world.group);
    this.world.dispose();
    this.physics.reset();
    this.currentLevel = level;
    this.world = new World(level, this.physics);
    this.scene.add(this.world.group);
    this.controller = new PandaController(this.physics, this.world, this.scoring, this.controllerEvents);
    this.lastState = this.controller.state;
    this.camHint = -1;
    this.chase.snap(this.controller.state.pos, this.world.startFrame());
  }

  private camHint = -1;

  /**
   * Keep the chase cam from clipping: pull it in front of any wall/hill between
   * the ball and the camera, and never let it drop below the nearby track
   * surface (fixes the under-track view on start/retry and behind-wall clipping).
   */
  private resolveCamera(camPos: THREE.Vector3, ballPos: THREE.Vector3): void {
    const RAPIER = this.physics.RAPIER;
    const origin = ballPos.clone().addScaledVector(new THREE.Vector3(0, 1, 0), 0.8);
    const toCam = camPos.clone().sub(origin);
    const dist = toCam.length();
    if (dist > 0.01) {
      const dir = toCam.multiplyScalar(1 / dist);
      const ray = new RAPIER.Ray(
        { x: origin.x, y: origin.y, z: origin.z },
        { x: dir.x, y: dir.y, z: dir.z },
      );
      const hit = this.physics.world.castRay(
        ray,
        dist,
        true,
        undefined,
        undefined,
        undefined,
        this.controller.body,
      );
      if (hit && hit.timeOfImpact < dist) {
        const d = Math.max(1.5, hit.timeOfImpact - 0.4);
        camPos.copy(origin).addScaledVector(dir, d);
      }
    }

    // Clamp above the local track surface. Use the higher of the centerline
    // surface and the ball itself, so on a raised "high lane" ridge the camera
    // can't sink below the ledge (the spline projects to the low centerline).
    const proj = this.world.spline.project(camPos, this.camHint);
    this.camHint = proj.sampleIndex;
    const minY = Math.max(proj.surfacePoint.y + 3, ballPos.y - 0.5);
    if (camPos.y < minY) camPos.y = minY;
  }

  private makeSkyTexture(top: number, bottom: number): THREE.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, '#' + top.toString(16).padStart(6, '0'));
    grad.addColorStop(1, '#' + bottom.toString(16).padStart(6, '0'));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 2, 256);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  private onDeviceChange(device: string): void {
    const touch = device === 'touch';
    // Only show the touch controls during play (never over the menus).
    this.input.touch.setVisible(touch && this.state === 'playing');
    if (this.state === 'playing') {
      this.hud.setPrompt(
        touch ? '◀ ▶ steer · tap to jump' : 'A/D steer · Space jump · Esc pause',
      );
    }
  }

  private popupAt(worldPos: THREE.Vector3, text: string, color: string): void {
    const v = worldPos.clone().project(this.chase.camera);
    const x = (v.x * 0.5 + 0.5) * 100;
    const y = (-v.y * 0.5 + 0.5) * 100;
    if (v.z > 1) return; // behind camera
    this.hud.popup(text, color, clamp(x, 2, 98), clamp(y, 5, 90));
  }

  private mountTitlePanda(): void {
    const el = document.getElementById('title-panda');
    if (el) this.pandaViewer.mount(el, 'neutral');
  }

  private async startRun(): Promise<void> {
    this.pandaViewer.unmount();
    try {
      await this.audio.start();
    } catch {
      /* audio is best-effort; never block starting the game */
    }
    void this.gate.tryLock();
    this.scoring.reset();
    this.mood.reset();
    this.world.resetCollectibles();
    this.world.resetBamboo();
    this.controller.spawnAtStart();
    this.chase.snap(this.controller.state.pos, this.world.startFrame());
    this.runTime = 0;
    this.acc = 0;
    this.state = 'playing';
    this.hud.show();
    this.menus.showPlaying();
    this.onDeviceChange(this.input.lastDevice);
  }

  private finish(): void {
    if (this.state !== 'playing') return;
    this.state = 'finished';
    const breakdown = this.scoring.finalize(this.currentLevel.parTime, this.runTime);
    this.audio.finish();
    this.fx.confetti(this.controller.state.pos, 220);
    this.hud.hide();
    this.input.touch.setVisible(false);
    this.menus.showResults(breakdown, this.runTime, this.mood.value);
    const el = document.getElementById('result-panda');
    // Only at the end do we show how the panda feels: party / happy / neutral / sad.
    const m = this.mood.value;
    const endExpression = m >= 0.78 ? 'party' : m >= 0.6 ? 'content' : m <= 0.35 ? 'dazed' : 'neutral';
    if (el) this.pandaViewer.mount(el, endExpression);
  }

  private quitToTitle(): void {
    this.state = 'title';
    this.scoring.reset();
    this.world.resetCollectibles();
    this.world.resetBamboo();
    this.controller.spawnAtStart();
    this.chase.snap(this.controller.state.pos, this.world.startFrame());
    this.hud.hide();
    this.menus.showTitle();
    this.mountTitlePanda();
    this.input.touch.setVisible(false);
  }

  private togglePause(): void {
    if (this.state === 'playing') this.pause();
    else if (this.state === 'paused') this.resume();
  }

  private pause(): void {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.input.touch.setVisible(false);
    this.menus.showPause();
  }

  private resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'playing';
    this.menus.hidePause();
    this.input.touch.setVisible(this.touchCapable && this.input.lastDevice === 'touch');
    this.last = performance.now();
  }

  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.chase.resize(w / h);
  }

  private tick(now: number): void {
    requestAnimationFrame((t) => this.tick(t));
    let dt = (now - this.last) / 1000;
    this.last = now;
    if (dt > 0.1) dt = 0.1; // clamp after tab-switch / hitches
    this.clock += dt;

    this.input.update();

    if (this.input.consumePause()) this.togglePause();

    if (this.state === 'playing') {
      this.acc += dt;
      let steps = 0;
      let firstStep = true;
      while (this.acc >= FIXED_STEP && steps < MAX_SUBSTEPS) {
        const jump = firstStep && this.input.consumeJump();
        this.lastState = this.controller.update(FIXED_STEP, {
          steer: this.input.steerAxis,
          jump,
        });
        this.physics.step();
        this.drainCollisions();
        this.runTime += FIXED_STEP;
        this.acc -= FIXED_STEP;
        firstStep = false;
        steps += 1;
      }
      if (steps === MAX_SUBSTEPS) this.acc = 0; // avoid spiral of death
    }

    this.updateVisuals(dt);
    this.renderer.render(this.scene, this.chase.camera);
  }

  private drainCollisions(): void {
    const ball = this.controller.collider.handle;
    this.physics.drainCollisions((h1, h2, started) => {
      if (!started) return;
      const other = h1 === ball ? h2 : h2 === ball ? h1 : -1;
      if (other >= 0 && this.world.obstacleHandles.has(other)) {
        this.controller.handleObstacleContact();
      }
    });
  }

  private updateVisuals(dt: number): void {
    const s = this.lastState;

    // Panda follows the physics ball.
    this.panda.root.position.copy(s.pos);
    this.panda.setBodyRotation(s.quat);
    this.panda.update(dt, s.speed01);

    // Camera.
    this.chase.update(
      dt,
      s.pos,
      s.velocity,
      s.speed01,
      this.world.spline.frameAt(s.t),
      this.input.cameraNudge,
      (camPos, ballPos) => this.resolveCamera(camPos, ballPos),
    );

    // Sun follows the ball so the shadow frustum stays tight.
    if (!this.isMobile) {
      this.sun.position.set(s.pos.x + 20, s.pos.y + 45, s.pos.z + 12);
      this.sun.target.position.copy(s.pos);
      this.sun.target.updateMatrixWorld();
    }

    // World + particles.
    this.world.update(dt, this.clock);
    this.fx.update(dt);

    // Dust trail on fast grounded sections (§9).
    if (this.state === 'playing' && s.grounded && s.speed01 > 0.28) {
      const ground = s.pos.clone().addScaledVector(new THREE.Vector3(0, 1, 0), -BALL_RADIUS * 0.6);
      this.fx.trail(ground, new THREE.Color(s.surface.color), s.speed01);
    }

    // Audio rumble tracks speed + surface.
    this.audio.updateRumble(s.speed01, s.grounded, s.surface);

    // HUD.
    if (this.state === 'playing') {
      this.scoring.update(dt, s.airborne);
      this.mood.update(dt);
      this.hud.setScore(this.scoring.liveScore);
      this.hud.setTime(this.runTime);
      this.hud.setCombo(this.scoring.streak, this.scoring.currentMultiplier, this.scoring.comboFraction);
      this.hud.setMood(this.mood.value);
      this.hud.setSpeedFx(s.speed01);
    }
  }
}
